/* import __COLOCATED_TEMPLATE__ from './error.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Constants, { AMP_TYPES } from '../../utils/constants';
import Utils from '../../utils/utils';
import {
  findInSignalChain,
  addChartFn,
  getVoltRefErrors,
  getDacErrors,
  getInvertingAmpErrors,
  getOutAmpErrors,
} from '../../utils/error-utils';

export default class Error extends Component {
  @service intl;

  @action getErrorData() {
    // options: ["voltage-reference", "non-inverting", "inverting", "output"]
    // from this.model.topologyInputHandler.current.map(t=>t.config.name)
    let findInTopologies = (name) => this.args.model.topologyInputHandler.current.find((t) => t.config.name === name), // can't trust ID here, take note;
      findInResistors = (name) => this.args.model.resistorHandler.currentResistors.find((t) => t.name === name);

    const dacSelected = this.args.model.signalChainHandler.dac.selected,
      currentSignalChain = this.args.model.signalChainHandler.currentItems,
      voltRef = currentSignalChain.find((item) => item.isVoltageReference),
      voltRefErrors = getVoltRefErrors(voltRef.selected, dacSelected, this.args.model);

    let nonInvertingAmpErrors = null;
    {
      const nonInvAmp = findInSignalChain(currentSignalChain, 'non inv amp'),
        nonInvAmpResistors = findInResistors('non-inverting-amplifier'); // check these out too...

      if (nonInvAmp && nonInvAmpResistors) {
        const nonInvertingAmpErrorsObj = getInvertingAmpErrors(
          AMP_TYPES.NON_INVERTING,
          nonInvAmp,
          this.args.model.signalChainHandler.inputVoltage, // ie output of Voltage Reference too
          dacSelected,
          this.args.model,
          nonInvAmpResistors,
        );
        nonInvertingAmpErrors = nonInvertingAmpErrorsObj.errors;
      }
    }

    let nonInvBufferErrors = null;
    {
      const nonInvBuffer = findInSignalChain(currentSignalChain, 'non inv buffer');
      const nonInvBufferResistors = findInResistors('non-inverting-amplifier') || {}; // this may be empty, but the buffer should still be handled

      if (nonInvBuffer) {
        const { errors } = getInvertingAmpErrors(
          AMP_TYPES.BUFFER,
          nonInvBuffer,
          this.args.model.positiveVoltage, // input voltage
          dacSelected,
          this.args.model,
          nonInvBufferResistors,
        );
        nonInvBufferErrors = errors;
      }
    }

    let invertingAmpErrors = null;
    {
      const invAmp = findInSignalChain(currentSignalChain, 'inv amp');
      const invAmpResistors = findInResistors('inverting-amplifier');

      if (invAmp && invAmpResistors) {
        const invertingAmpErrorsObj = getInvertingAmpErrors(
          AMP_TYPES.INVERTING,
          invAmp,
          this.args.model.signalChainHandler.inputVoltage,
          dacSelected,
          this.args.model,
          invAmpResistors,
        );
        invertingAmpErrors = invertingAmpErrorsObj.errors;
      }
    }

    let invBufferErrors = null;
    {
      const invBuffer = findInSignalChain(currentSignalChain, 'inv buffer');
      const invBufferResistors = findInResistors('inverting-amplifier') || {}; // this may be empty, but the buffer should still be handled

      if (invBuffer) {
        const { errors } = getInvertingAmpErrors(
          AMP_TYPES.BUFFER,
          invBuffer,
          this.args.model.negativeVoltage,
          dacSelected,
          this.args.model,
          invBufferResistors,
        );
        invBufferErrors = errors;
      }
    }

    // DAC
    const dacOutputVoltage = Utils.isNumeric(this.args.model.positiveVoltage)
      ? this.args.model.positiveVoltage
      : this.args.model.signalChainHandler.inputVoltage;
    const dacErrors = getDacErrors(dacSelected, dacOutputVoltage, this.args.model, {
      getMultiplyingBwFromSettlingTime: true,
    });

    let outAmpErrors = null;
    let outAmpType = null;
    {
      let outAmp = findInSignalChain(currentSignalChain, 'output amp');
      if (!outAmp) {
        outAmp = findInSignalChain(currentSignalChain, 'output buffer'); // when buffer, value will be here instead
      }
      const outAmpTopology = findInTopologies('output'); // TODO: Use constants here
      const outAmpResistor = findInResistors('output-amplifier') || {}; // In "none" or "buffered" this will be empty

      const outAmpTypeId = outAmpTopology.selectedOption.id;
      if (outAmpTypeId === 'buffer') outAmpType = AMP_TYPES.BUFFER;
      if (outAmpTypeId === 'external resistors') outAmpType = AMP_TYPES.NON_INVERTING;

      if (outAmpType) {
        const outAmpErrorsObj = getOutAmpErrors(
          outAmpType,
          outAmp,
          dacSelected,
          this.args.model,
          outAmpResistor,
          dacOutputVoltage,
          outAmpType !== AMP_TYPES.BUFFER ? true : false, // showToleranceAndTempCoErrors = true if it's not a buffer
        );
        outAmpErrors = outAmpErrorsObj.outAmpErrors;
      }
    }

    let chartInfo = { charts: [] }; // Array of all charts
    let addChart = addChartFn(chartInfo.charts);
    const outAmpOrBufferLabel =
      outAmpType == AMP_TYPES.BUFFER
        ? this.intl.t('labels.parts.output_buffer')
        : this.intl.t('labels.parts.output_amplifier');
    const outAmpOrBufferClass =
      outAmpType == AMP_TYPES.BUFFER ? Constants.SVG_PARTS.output_buffer : Constants.SVG_PARTS.output_amplifier;
    addChart(this.intl.t('labels.parts.voltage_reference'), Constants.SVG_PARTS.voltage_reference, voltRefErrors);
    addChart(
      this.intl.t('labels.parts.non_inverting_amplifier'),
      Constants.SVG_PARTS.non_inverting_amplifier,
      nonInvertingAmpErrors,
    );
    addChart(
      this.intl.t('labels.parts.non_inverting_buffer'),
      Constants.SVG_PARTS.non_inverting_buffer,
      nonInvBufferErrors,
    );
    addChart(this.intl.t('labels.parts.voltage_output_dac'), Constants.SVG_PARTS.voltage_output_dac, dacErrors);
    addChart(
      this.intl.t('labels.parts.inverting_amplifier'),
      Constants.SVG_PARTS.inverting_amplifier,
      invertingAmpErrors,
    );
    addChart(this.intl.t('labels.parts.inverting_buffer'), Constants.SVG_PARTS.inverting_buffer, invBufferErrors);
    addChart(outAmpOrBufferLabel, outAmpOrBufferClass, outAmpErrors);

    chartInfo.hasIdealVoltage = true;

    return chartInfo;
  }
}
